import {ApiSearchParameters, SearchParameters} from '@findhotel/sapi'
import {uniq} from 'ramda'

import {MappingContext} from '../../../offer/business/offersMapping'
import {numberOfRooms} from '../../../room/business/roomConfiguration'

import type {SearchData, SearchState} from '.'
import type {TransformedSearchResults} from './transformations/transformSearchResults'
import type {ImageCategory} from '@findhotel/sapi'
import type {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src'
import type {MaybeDrafted} from '@reduxjs/toolkit/dist/query/core/buildThunks'

export type BaseMappingContext = Omit<
  MappingContext,
  // We get these params from SAPI responses in SAPI.search and SAPI.offers
  'checkIn' | 'checkOut' | 'numberOfRooms'
>

/**
 * Merge hotelIds in state with newly supplied hotelIds - ensuring they are unique
 * @param stateIds - Array of existing hotel IDs from state
 * @param responseIds - Array of new hotel IDs from response
 * @returns Array of unique hotel IDs
 */
export const mergeHotelIds = (
  stateIds: string[] = [],
  responseIds?: string[]
) => (responseIds ? uniq([...stateIds, ...responseIds]) : stateIds)

/**
 * Merge the current state (Immer draft) with new response data & current searchState
 * @param draft - The current search data draft state
 * @param root0 - Transformed search results object
 * @param root0.status - Status of the search
 * @param root0.hotelIds - Array of hotel IDs
 * @param root0.anchorHotelId - ID of the anchor hotel
 * @param root0.hotelEntities - Hotel entities data
 * @param root0.hotelOfferEntities - Hotel offer entities data
 * @param searchState - Current search state
 */
export const mergeDataRecipe = (
  draft: MaybeDrafted<SearchData>,
  {
    status,
    hotelIds,
    anchorHotelId,
    hotelEntities,
    hotelOfferEntities,
    ...base
  }: Partial<TransformedSearchResults>,
  searchState?: SearchState
) => ({
  currentSearch: draft.currentSearch,
  ...base,
  status: status ?? draft.status,
  searchState: searchState ?? draft.searchState,
  hotelIds: mergeHotelIds(draft.hotelIds, hotelIds),
  anchorHotelId: anchorHotelId ?? draft.anchorHotelId,
  hotelEntities: {...draft.hotelEntities, ...hotelEntities},
  hotelOfferEntities: {
    ...draft.hotelOfferEntities,
    ...hotelOfferEntities
  }
})

/**
 * Helper to narrow the SearchData type to one with results - ideally SearchData would be a union that narrows itself
 * @param data - The search data to check for results
 * @returns True if the data has search results
 */
export const hasSearchResults = (
  data: SearchData
): data is Required<SearchData> =>
  !!(data.hotelIds && data.hotelEntities && data.hotelOfferEntities)

/**
 * Type guard to validate whether params *returned from* SAPI are complete
 * @param params - as returned in searchResponse.searchParameters in SAPI.search() callbacks
 * @returns boolean - narrows the type to SearchParameters
 */
export const isValidResponseSearchParams = (
  params: ApiSearchParameters | SearchParameters | undefined
): params is SearchParameters =>
  !!(params && params.checkIn && params.checkOut && params.rooms)

/**
 * Compose base mapping options with search params
 * @param baseMappingContext - mapping options from app/user context
 * @param currentParameters - (validated) search parameters for the current search
 */
export const createMappingContext = (
  baseMappingContext: BaseMappingContext,
  currentParameters: SearchParameters
) => ({
  ...baseMappingContext,
  checkIn: currentParameters.checkIn,
  checkOut: currentParameters.checkOut,
  numberOfRooms: numberOfRooms(currentParameters.rooms)
})

// Category-based selection logic
export const CATEGORY_ORDER: {
  id: ImageCategory['id']
  max: number
}[] = [
  {id: '21001', max: 2}, // Room
  {id: '22013', max: 1}, // View from room
  {id: '10002', max: 1}, // Reception
  {id: '30000', max: 1}, // Pool
  {id: '81003', max: 1}, // Restaurant
  {id: '23000', max: 1}, // Bathroom
  {id: '41000', max: 1}, // Fitness facility
  {id: '41001', max: 1}, // Gym
  {id: '51000', max: 1}, // Spa
  {id: '22000', max: 1}, // Common area
  {id: '10001', max: 1} // Lobby
]

export function sortMosaicImages(
  images: ContentHotel['images'] = []
): ContentHotel['images'] {
  const hero = images[0]
  // For each category, get up to max images for that category
  const categorized = [
    ...(hero ? [hero] : []),
    ...CATEGORY_ORDER.map(({id, max}) =>
      images
        .filter(img => img.category.some(cat => cat.id === id))
        .slice(0, max)
    )
  ]
  const rest = images.filter(img => !categorized.flat().includes(img))
  // Flatten all arrays to get the sorted images
  return [...categorized, rest].flat()
}
